import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { IMG } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";
import { pageSchema } from "@/lib/schema";

const ABOUT_DESC = "The story behind First Mover OS: from dropshipping fail to the First Mover Method, as a single mother with a biotech vision.";

const CHAPTERS = [
  { year: "Chapter 01", title: "The dropshipping years", text: "Like many beginners, I started where everyone starts: dropshipping. I copied products that were already trending, spent money on ads and arrived late, every single time." },
  { year: "Chapter 02", title: "The lesson in losing", text: "The problem was never effort. It was timing. By the time an idea is everywhere, the opportunity is gone. I started studying the signals that come before a trend." },
  { year: "Chapter 03", title: "Building as a single mother", text: "Raising a child alone meant I could not afford trial and error. Every hour and every franc had to count. So I turned what I learned into a repeatable system." },
  { year: "Chapter 04", title: "The First Mover Method", text: "That system became First Mover OS: a clear way to spot a shift early, choose an idea with intention and build a first offer before the crowd arrives." },
  { year: "What is next", title: "The biotech vision", text: "My long-term goal is to build in biotech. First Mover OS is part of that path, and proof that you do not need a perfect starting position to build something real." },
];

export default function About() {
  useSeo("About Dilara", ABOUT_DESC, pageSchema("AboutPage", "About Dilara", "/about", ABOUT_DESC, { about: { "@type": "Person", name: "Dilara" } }));
  return (
    <main data-testid="about-page" className="pt-28 md:pt-36">
      <section className="mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="eyebrow">About Dilara</p>
          <h1 className="font-display mt-6 text-5xl font-medium leading-[1.02] tracking-[-0.03em] text-white md:text-7xl">
            <MaskedLines lines={["I was not early.", "I was late,", <span key="l" className="font-serif-i text-zinc-400">until I learned why.</span>]} />
          </h1>
          <p className="mt-10 max-w-xl text-lg leading-relaxed text-zinc-300">
            I am Dilara, founder of First Mover OS. This is the honest version of how I got here, without the rented Lamborghini.
          </p>
        </div>
        <motion.div className="lg:col-span-5" initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}>
          <div className="aspect-[4/5] overflow-hidden border border-line">
            <img src={IMG.founder} alt="Dilara, founder of First Mover OS" className="h-full w-full object-cover object-top" data-testid="about-portrait" />
          </div>
        </motion.div>
      </section>
      <section className="mx-auto max-w-5xl px-6 py-24 md:px-12 md:py-40">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.title}>
            <article data-testid={`about-chapter-${i}`} className="grid gap-4 border-t border-line py-12 md:grid-cols-12 md:gap-10">
              <p className="eyebrow md:col-span-3">{c.year}</p>
              <div className="md:col-span-9">
                <h2 className="font-display text-3xl font-medium tracking-tight text-white md:text-4xl">{c.title}</h2>
                <p className="mt-5 text-base leading-relaxed text-zinc-300 md:text-lg">{c.text}</p>
              </div>
            </article>
          </Reveal>
        ))}
        <Reveal className="mt-16 border-t border-line pt-16">
          <p className="font-display max-w-3xl text-3xl leading-snug text-white md:text-4xl">
            If I could build this with a child on my hip, you can start with a few focused hours a week.
          </p>
          <Link to="/products/bundle" data-testid="about-cta-button" className="btn-cta mt-10">Start with the Bundle <ArrowRight size={18} /></Link>
        </Reveal>
      </section>
    </main>
  );
}
